// Local persistence (IndexedDB). Everything stays on the device: calibrations,
// sessions, attempts with their raw landmark timelines (so they can be
// re-scored when the algorithm improves), video clips and evidence stills.

import type { AttemptAnalysis } from '../core/analysis';
import type { PlaneMapping, Stance, TapeLayout } from '../core/mapping';
import type { AnalysisParams, Strictness } from '../core/params';
import type { Reference } from '../core/reference';
import type { TruthLabel } from '../core/stats';

export type ModelVariant = 'lite' | 'full' | 'heavy';
export type CueMode = 'auto' | 'timer' | 'none';

export interface Settings {
  strictness: Strictness;
  holdMs: number;
  attemptsPerSet: number;
  cueMode: CueMode;
  randomDelay: boolean;
  voice: boolean;
  beeps: boolean;
  voiceURI: string | null;
  speechRate: number;
  announceTime: boolean;
  checkAngle: boolean;
  model: ModelVariant;
  delegate: 'GPU' | 'CPU';
  cameraId: string | null;
  resolution: 720 | 1080;
  fps: 30 | 60;
  keepClipSessions: number;
  showSkeleton: boolean;
  activeCalibrationId: string | null;
}

export const DEFAULT_SETTINGS: Settings = {
  strictness: 'standard',
  holdMs: 1000,
  attemptsPerSet: 10,
  cueMode: 'auto',
  randomDelay: true,
  voice: true,
  beeps: true,
  voiceURI: null,
  speechRate: 1.05,
  announceTime: false,
  checkAngle: false,
  model: 'full',
  delegate: 'GPU',
  cameraId: null,
  resolution: 720,
  fps: 30,
  keepClipSessions: 15,
  showSkeleton: true,
  activeCalibrationId: null,
};

export interface Anchor {
  /** Normalized image position. */
  x: number;
  y: number;
  /** Tape ends can be covered by a rider lying on the board; scenery can't. */
  kind?: 'tape' | 'scene';
  /** Grayscale patch from a frame downscaled to ANCHOR_WIDTH. */
  patch: Uint8Array;
}

export interface CalibrationRecord {
  id: string;
  createdAt: number;
  stance: Stance;
  source: 'camera' | 'demo';
  camera: { deviceId: string | null; label: string | null; width: number; height: number };
  mapping: PlaneMapping;
  /** Present for tape calibrations (normalized taps + dimensions). */
  tape: TapeLayout | null;
  /** Present for tape-free calibrations. */
  stanceWidth: number | null;
  reference: Reference;
  anchors: Anchor[];
  warnings: string[];
  /** Blob key of the frame used to mark the tape (empty board). */
  boardStillKey: string | null;
  /** Blob key of a frame of the rider holding the stance. */
  stanceStillKey: string | null;
}

export interface SessionRecord {
  id: string;
  calibrationId: string;
  startedAt: number;
  endedAt: number | null;
  mode: 'practice' | 'experiment';
  source: 'camera' | 'demo';
  params: AnalysisParams;
  model: ModelVariant;
  cueMode: CueMode;
  attemptIds: string[];
}

export interface Still {
  key: string;
  /** Timeline time of the frame, ms. */
  t: number;
  /** Landmarks of exactly that frame (for an exact overlay). */
  lm: Float32Array | null;
}

export interface AttemptRecord {
  id: string;
  sessionId: string;
  calibrationId: string;
  index: number;
  createdAt: number;
  /** Serialized PoseFrame[] (see core/pose). */
  frames: Float32Array;
  width: number;
  height: number;
  cueTime: number | null;
  analysis: AttemptAnalysis;
  /** Who produced the frames: the live loop or a frame-by-frame re-analysis. */
  source: 'live' | 'offline';
  model: ModelVariant | 'sim';
  videoKey: string | null;
  videoMime: string | null;
  /** Timeline time (ms) at which the video starts. */
  videoOffsetMs: number;
  stills: { landing: Still | null; settled: Still | null };
  /** What the rider was asked to do (experiment mode). */
  scenario: string | null;
  label: TruthLabel | null;
  note: string;
  /** Frame-by-frame re-analyses of the clip with other models (validation lab). */
  alt?: Partial<Record<ModelVariant, { frames: Float32Array; analysis: AttemptAnalysis; createdAt: number }>>;
}

export interface BlobRecord {
  key: string;
  blob: Blob;
}

const DB_NAME = 'surfdaddy';
const DB_VERSION = 1;

let dbPromise: Promise<IDBDatabase> | null = null;

function open(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      db.createObjectStore('kv');
      db.createObjectStore('calibrations', { keyPath: 'id' });
      const s = db.createObjectStore('sessions', { keyPath: 'id' });
      s.createIndex('startedAt', 'startedAt');
      const a = db.createObjectStore('attempts', { keyPath: 'id' });
      a.createIndex('sessionId', 'sessionId');
      a.createIndex('createdAt', 'createdAt');
      db.createObjectStore('blobs', { keyPath: 'key' });
    };
    req.onsuccess = () => {
      const d = req.result;
      // iOS can drop the connection (e.g. after backgrounding); reopen next time.
      d.onclose = () => {
        if (dbPromise === promise) dbPromise = null;
      };
      d.onversionchange = () => {
        d.close();
        if (dbPromise === promise) dbPromise = null;
      };
      resolve(d);
    };
    req.onerror = () => reject(req.error);
    req.onblocked = () => reject(new Error('Database upgrade blocked by another open tab.'));
  });
  const promise = dbPromise;
  promise.catch(() => {
    if (dbPromise === promise) dbPromise = null;
  });
  return promise;
}

type StoreName = 'kv' | 'calibrations' | 'sessions' | 'attempts' | 'blobs';

/** Runs one request in its own transaction and resolves once the transaction commits. */
async function tx<T>(store: StoreName, mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  try {
    return await txOnce(store, mode, fn);
  } catch (e) {
    // A connection that died under us: reopen once and retry.
    if ((e as DOMException)?.name !== 'InvalidStateError') throw e;
    dbPromise = null;
    return txOnce(store, mode, fn);
  }
}

async function txOnce<T>(store: StoreName, mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const d = await open();
  return new Promise<T>((resolve, reject) => {
    const t = d.transaction(store, mode);
    const req = fn(t.objectStore(store));
    let result: T;
    req.onsuccess = () => (result = req.result);
    req.onerror = () => reject(req.error);
    t.oncomplete = () => resolve(result);
    t.onerror = () => reject(t.error);
    t.onabort = () => reject(t.error ?? new Error('Transaction aborted'));
  });
}

export const uid = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

// --- Settings ---------------------------------------------------------------

export async function loadSettings(): Promise<Settings> {
  const s = await tx<Partial<Settings> | undefined>('kv', 'readonly', (st) => st.get('settings'));
  return { ...DEFAULT_SETTINGS, ...(s ?? {}) };
}

export async function saveSettings(s: Settings): Promise<void> {
  await tx('kv', 'readwrite', (st) => st.put(s, 'settings'));
}

// --- Generic records ----------------------------------------------------------

export const db = {
  getCalibration: (id: string) => tx<CalibrationRecord | undefined>('calibrations', 'readonly', (s) => s.get(id)),
  putCalibration: (c: CalibrationRecord) => tx('calibrations', 'readwrite', (s) => s.put(c)),
  listCalibrations: () => tx<CalibrationRecord[]>('calibrations', 'readonly', (s) => s.getAll()),
  deleteCalibration: (id: string) => tx('calibrations', 'readwrite', (s) => s.delete(id)),

  getSession: (id: string) => tx<SessionRecord | undefined>('sessions', 'readonly', (s) => s.get(id)),
  putSession: (r: SessionRecord) => tx('sessions', 'readwrite', (s) => s.put(r)),
  listSessions: async () => (await tx<SessionRecord[]>('sessions', 'readonly', (s) => s.getAll())).sort((a, b) => b.startedAt - a.startedAt),

  getAttempt: (id: string) => tx<AttemptRecord | undefined>('attempts', 'readonly', (s) => s.get(id)),
  putAttempt: (r: AttemptRecord) => tx('attempts', 'readwrite', (s) => s.put(r)),
  attemptsForSession: async (sessionId: string) =>
    (await tx<AttemptRecord[]>('attempts', 'readonly', (s) => s.index('sessionId').getAll(sessionId))).sort((a, b) => a.index - b.index),
  allAttempts: () => tx<AttemptRecord[]>('attempts', 'readonly', (s) => s.getAll()),

  getBlob: async (key: string) => (await tx<BlobRecord | undefined>('blobs', 'readonly', (s) => s.get(key)))?.blob ?? null,
  putBlob: async (blob: Blob) => {
    const key = uid();
    await tx('blobs', 'readwrite', (s) => s.put({ key, blob }));
    return key;
  },
  deleteBlob: (key: string) => tx('blobs', 'readwrite', (s) => s.delete(key)),
};

export async function deleteSession(id: string): Promise<void> {
  const attempts = await db.attemptsForSession(id);
  for (const a of attempts) await deleteAttempt(a);
  await tx('sessions', 'readwrite', (s) => s.delete(id));
}

export async function deleteAttempt(a: AttemptRecord): Promise<void> {
  for (const key of [a.videoKey, a.stills.landing?.key, a.stills.settled?.key]) if (key) await db.deleteBlob(key);
  await tx('attempts', 'readwrite', (s) => s.delete(a.id));
}

/** Drops video clips from sessions older than the newest `keep`, keeping results and stills. */
export async function pruneClips(keep: number): Promise<number> {
  const sessions = (await db.listSessions()).filter((s) => s.attemptIds.length > 0);
  let removed = 0;
  for (const s of sessions.slice(keep)) {
    for (const a of await db.attemptsForSession(s.id)) {
      if (!a.videoKey) continue;
      await db.deleteBlob(a.videoKey);
      await db.putAttempt({ ...a, videoKey: null, videoMime: null });
      removed++;
    }
  }
  return removed;
}

export async function storageEstimate(): Promise<{ usage: number; quota: number; persisted: boolean } | null> {
  if (!navigator.storage?.estimate) return null;
  const { usage = 0, quota = 0 } = await navigator.storage.estimate();
  const persisted = (await navigator.storage.persisted?.()) ?? false;
  return { usage, quota, persisted };
}

export async function requestPersistence(): Promise<boolean> {
  try {
    return (await navigator.storage?.persist?.()) ?? false;
  } catch {
    return false;
  }
}

export async function wipeEverything(): Promise<void> {
  const d = await open();
  d.close();
  dbPromise = null;
  await new Promise<void>((resolve, reject) => {
    const req = indexedDB.deleteDatabase(DB_NAME);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

